import {
  ProtocolConnection,
  PublishLocationsResponse,
  PublishTariffResponse,
  ConnectionType,
  CpoType,
} from './api-types';

/**
 * Connection status for the operator - now based on actual connections from API
 */
export interface ConnectionStatus {
  operatorId: number;
  operatorName: string;
  connections: ProtocolConnection[];
  hasConnections: boolean;
  lastChecked: Date;
  /** CPO type: internal (Nayax manages) or external (operator's own OCPI system) */
  cpoType: CpoType | null;
  /** OCPI Party identification - available after configuration */
  ocpiId?: {
    partyId: string;
    countryCode: string | null;
  };
}

/**
 * Get display name for connection type
 */
export const getConnectionTypeName = (type: ConnectionType): string => {
  const names: Record<ConnectionType, string> = {
    OCPI: 'OCPI Connection',
    Hubject: 'Hubject (OICP)',
    Gireve: 'Gireve',
    Kiosk: 'Kiosk',
    OcpiToVpost: 'OCPI to VPOST',
  };
  return names[type] || type;
};

/**
 * Get connection status badge text
 */
export const getConnectionStatusText = (registered: boolean): string => {
  return registered ? 'Registered' : 'Pending';
};

/**
 * Get connection status color classes
 */
export const getConnectionStatusColor = (registered: boolean): {
  bg: string;
  text: string;
  dot: string;
} => {
  if (registered) {
    return {
      bg: 'bg-green-50',
      text: 'text-green-700',
      dot: 'bg-green-500',
    };
  }
  return {
    bg: 'bg-yellow-50',
    text: 'text-yellow-700',
    dot: 'bg-yellow-500',
  };
};

// ============================================================================
// Legacy Types (kept for backward compatibility during transition)
// ============================================================================

export interface ServiceStatus {
  isConfigured: boolean;
  isActive: boolean;
  status: 'CONNECTED' | 'NOT_CONFIGURED' | 'ERROR' | 'PENDING';
  statusMessage: string;
  details: ServiceDetails | null;
  error: string | null;
}

export interface ServiceDetails {
  connectionId?: string;
  lastSync?: Date;
  configuration?: Record<string, unknown>;
  partyId?: string;
  countryCode?: string;
  name?: string;
}

export type HealthStatus = 'HEALTHY' | 'DEGRADED' | 'CRITICAL' | 'UNKNOWN';

/**
 * @deprecated Use ConnectionStatus.connections instead
 */
export const calculateOverallHealth = (
  ocpiCpoStatus: ServiceStatus,
  kioskStatus: ServiceStatus
): HealthStatus => {
  const ocpiHealthy = ocpiCpoStatus.status === 'CONNECTED';
  const kioskHealthy = kioskStatus.status === 'CONNECTED';

  if (ocpiHealthy && kioskHealthy) return 'HEALTHY';
  if (!ocpiHealthy && !kioskHealthy) return 'CRITICAL';
  return 'DEGRADED';
};

// ============================================================================
// Publish Result Types
// ============================================================================

export interface PublishResult {
  type: 'locations' | 'tariffs';
  isSuccess: boolean;
  timestamp: Date;
  response: PublishLocationsResponse | PublishTariffResponse;
  errorSummary?: string;
  errors?: string[];
}

export interface PublishLocationsResult extends PublishResult {
  type: 'locations';
  response: PublishLocationsResponse;
  successCount: number;
  failedCount: number;
  successMachineIds: number[];
  failedMachineIds: number[];
}

export interface PublishTariffsResult extends PublishResult {
  type: 'tariffs';
  response: PublishTariffResponse;
}

/**
 * Helper to create a PublishLocationsResult from API response
 */
export const createPublishLocationsResult = (
  response: PublishLocationsResponse
): PublishLocationsResult => ({
  type: 'locations',
  isSuccess: response.isSuccess,
  timestamp: new Date(),
  response,
  successCount: response.successMachineIds?.length ?? 0,
  failedCount: response.failedMachineIds?.length ?? 0,
  successMachineIds: response.successMachineIds ?? [],
  failedMachineIds: response.failedMachineIds ?? [],
  errorSummary: response.isSuccess
    ? undefined
    : response.errorMessage || `${response.failedMachineIds?.length ?? 0} machines failed`,
  errors: response.errors ?? [],
});

/**
 * Helper to create a PublishTariffsResult from API response
 */
export const createPublishTariffsResult = (
  response: PublishTariffResponse
): PublishTariffsResult => ({
  type: 'tariffs',
  isSuccess: response.isSuccess,
  timestamp: new Date(),
  response,
  errorSummary: response.isSuccess ? undefined : response.errorMessage || 'Publish failed',
  errors: response.errors ?? [],
});
